import React, { useState, useEffect } from 'react';
import Button from '../UI/Button';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { createGoal, updateGoal, deleteGoal, fetchProjectGoals, Goal } from '../../store/slices/goalsSlice';
import { Milestone } from './Milestones/index';
import {
  GoalCard,
  GoalForm,
  QuickValueUpdate,
  GoalFormData,
} from './Goals';

interface GoalsEditorProps {
  projectId?: string;
  milestones?: Milestone[];
  goals?: Goal[];
  onGoalsChange?: (goals: Goal[]) => void;
  disabled?: boolean;
}

const GoalsEditor: React.FC<GoalsEditorProps> = ({
  projectId,
  milestones = [],
  goals: propGoals,
  onGoalsChange,
  disabled = false,
}) => {
  const dispatch = useAppDispatch();
  const { items, projectGoals, isLoading } = useAppSelector((state) => state.goals);
  
  // Fetch goals if projectId is provided
  useEffect(() => {
    if (projectId && !propGoals) {
      dispatch(fetchProjectGoals({ projectId, includeDeleted: false }));
    }
  }, [dispatch, projectId, propGoals]);

  // Get goals from Redux store or props
  const goals = propGoals || (projectId && projectGoals[projectId]
    ? projectGoals[projectId].map((id) => items[id]).filter(Boolean)
    : []);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [goalForm, setGoalForm] = useState<GoalFormData>({
    title: '',
    description: '',
    targetValue: 0,
    currentValue: 0,
    unit: '',
    status: 'pending',
  });
  const [updateNotes, setUpdateNotes] = useState('');

  const handleFormDataChange = (data: Partial<GoalFormData>) => {
    setGoalForm((prev) => ({ ...prev, ...data }));
  };

  const handleAdd = async () => {
    if (!goalForm.title || !goalForm.targetValue || !goalForm.unit) return;
    if (!projectId) {
      // For new projects, just add to local state
      const newGoal: Partial<Goal> = {
        title: goalForm.title,
        description: goalForm.description,
        targetValue: goalForm.targetValue,
        currentValue: goalForm.currentValue,
        unit: goalForm.unit,
        progress: goalForm.targetValue > 0 ? (goalForm.currentValue / goalForm.targetValue) * 100 : 0,
        status: goalForm.status,
        milestoneId: goalForm.milestoneId,
      };
      if (onGoalsChange) {
        onGoalsChange([...goals, newGoal as Goal]);
      }
      setGoalForm({
        title: '',
        description: '',
        targetValue: 0,
        currentValue: 0,
        unit: '',
        status: 'pending',
      });
      setEditingIndex(null);
      return;
    }

    try {
      const result = await dispatch(
        createGoal({
          projectId,
          ...goalForm,
          milestoneId: goalForm.milestoneId || undefined,
        })
      );
      if (createGoal.fulfilled.match(result)) {
        setGoalForm({
          title: '',
          description: '',
          targetValue: 0,
          currentValue: 0,
          unit: '',
          status: 'pending',
        });
        setEditingIndex(null);
      }
    } catch (error) {
      console.error('Error creating goal:', error);
    }
  };

  const handleEdit = (index: number) => {
    const goal = goals[index];
    setEditingIndex(index);
    setGoalForm({
      _id: goal._id || goal.id,
      title: goal.title,
      description: goal.description || '',
      targetValue: goal.targetValue,
      currentValue: goal.currentValue,
      unit: goal.unit,
      status: goal.status,
      outcome: goal.outcome,
      outcomeNotes: goal.outcomeNotes || '',
      conclusion: goal.conclusion || '',
      results: goal.results || '',
      delayReason: goal.delayReason || '',
      postponeReason: goal.postponeReason || '',
      cancelReason: goal.cancelReason || '',
      milestoneId: typeof goal.milestoneId === 'string' ? goal.milestoneId : (goal.milestoneId?._id || goal.milestone?._id || ''),
    });
    setUpdateNotes('');
  };

  const handleSave = async () => {
    if (editingIndex === null || editingIndex < 0) return;
    const goal = goals[editingIndex];
    const goalId = goal._id || goal.id;
    
    if (!projectId || !goalId) {
      // For new projects, just update local state
      const updated = [...goals];
      updated[editingIndex] = {
        ...goal,
        ...goalForm,
      };
      setEditingIndex(null);
      setUpdateNotes('');
      if (onGoalsChange) {
        onGoalsChange(updated);
      }
      return;
    }

    try {
      const updateData: any = {
        ...goalForm,
      };
      if (updateNotes) {
        updateData.updateNotes = updateNotes;
      }
      const result = await dispatch(
        updateGoal({
          id: goalId,
          data: updateData,
        })
      );
      if (updateGoal.fulfilled.match(result)) {
        setEditingIndex(null);
        setUpdateNotes('');
      }
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setGoalForm({
      title: '',
      description: '',
      targetValue: 0,
      currentValue: 0,
      unit: '',
      status: 'pending',
    });
    setUpdateNotes('');
  };

  const handleDelete = async (index: number) => {
    const goal = goals[index];
    const goalId = goal._id || goal.id;
    if (!confirm(`Are you sure you want to delete "${goal.title}"?`)) return;

    if (!projectId || !goalId) {
      // For new projects, just remove from local state
      const updated = goals.filter((_, i) => i !== index);
      if (onGoalsChange) {
        onGoalsChange(updated);
      }
      return;
    }

    try {
      await dispatch(deleteGoal(goalId));
    } catch (error) {
      console.error('Error deleting goal:', error);
    }
  };

  const handleUpdateValue = async (index: number, newValue: number) => {
    const goal = goals[index];
    const goalId = goal._id || goal.id;
    
    if (!projectId || !goalId) {
      // For new projects, just update local state
      const updated = [...goals];
      updated[index] = {
        ...goal,
        currentValue: newValue,
        progress: goal.targetValue > 0 ? (newValue / goal.targetValue) * 100 : 0,
      };
      if (onGoalsChange) {
        onGoalsChange(updated);
      }
      return;
    }

    try {
      await dispatch(
        updateGoal({
          id: goalId,
          data: {
            currentValue: newValue,
            updateNotes: `Value updated from ${goal.currentValue} to ${newValue}`,
          } as any,
        })
      );
    } catch (error) {
      console.error('Error updating goal value:', error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Project Goals</h3>
        {!disabled && editingIndex === null && (
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setGoalForm({
                title: '',
                description: '',
                targetValue: 0,
                currentValue: 0,
                unit: '',
                status: 'pending',
              });
              setEditingIndex(-1);
            }}
          >
            Add Goal
          </Button>
        )}
      </div>

      {(editingIndex === -1 || editingIndex !== null) && !disabled && (
        <GoalForm
          formData={goalForm}
          onFormDataChange={handleFormDataChange}
          milestones={milestones}
          updateNotes={updateNotes}
          onUpdateNotesChange={setUpdateNotes}
          showUpdateNotes={editingIndex !== null && editingIndex >= 0}
          onSubmit={editingIndex === -1 ? handleAdd : handleSave}
          onCancel={handleCancel}
          isLoading={isLoading}
          submitLabel={editingIndex === -1 ? 'Add' : 'Save'}
        />
      )}

      {goals.length === 0 ? (
        <p className="text-gray-500 text-sm">No goals defined yet.</p>
      ) : (
        <div className="space-y-3">
          {goals.map((goal, index) => {
            const isEditing = editingIndex === index;

            return (
              <GoalCard
                key={goal._id || goal.id || index}
                goal={goal}
                showActions={!disabled && !isEditing}
                actions={
                  !isEditing ? (
                    <>
                      <QuickValueUpdate
                        currentValue={goal.currentValue}
                        onValueChange={(newValue) => handleUpdateValue(index, newValue)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(index)}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(index)}
                      >
                        Delete
                      </Button>
                    </>
                  ) : undefined
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GoalsEditor;

